import { useCallback, useEffect, useRef } from 'react';

export interface UseIntersectionOptions {
  /** Off while there is nothing left to fetch — no observer is created at all. */
  isEnabled?: boolean;
  /** Fire before the sentinel is actually visible, so the next page is already in flight. */
  rootMargin?: string;
  threshold?: number;
}

/**
 * A **callback ref** that fires `onIntersect` while the node it is attached to
 * is in view.
 *
 * A callback ref rather than the usual `{ ref }` object for a hard reason: React
 * Compiler's `refs` rule is on at error level and forbids reading `ref.current`
 * during render, which is exactly what `useEffect(() => observe(ref.current))`
 * ends up doing one indirection away. A callback ref is also strictly more
 * correct — React invokes it the moment the node mounts, so a sentinel inside a
 * list that renders after its first paint is observed immediately instead of one
 * effect pass late.
 *
 * `onIntersect` is held in a ref and called through it, so the returned callback
 * stays referentially stable. If it changed identity every render, React would
 * detach and re-attach the node on each pass, and each re-attach fires the
 * observer again on an already-visible sentinel — an infinite "load more".
 */
export function useIntersection(
  onIntersect: () => void,
  { isEnabled = true, rootMargin, threshold }: UseIntersectionOptions = {},
): (node: Element | null) => void {
  const callbackRef = useRef(onIntersect);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    callbackRef.current = onIntersect;
  }, [onIntersect]);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    [],
  );

  return useCallback(
    (node: Element | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (node === null || !isEnabled) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            callbackRef.current();
          }
        },
        { rootMargin, threshold },
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [isEnabled, rootMargin, threshold],
  );
}
